import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaChevronDown } from "react-icons/fa";
import { SlotsFilter } from "../slots.service";

type SelectOption = {
  value: string;
  text: string;
};

export type SearchItemConfig = {
  type: "input" | "select";
  placeholder: string;
  options?: SelectOption[];
};

type Props = {
  updateQuery: (filter: SlotsFilter) => void;
  initialQuery?: SlotsFilter;
};

const searchItems: Record<string, SearchItemConfig> = {
  slotId: {
    type: "input",
    placeholder: "Enter slot ID",
  },
  userId: {
    type: "input",
    placeholder: "Enter user ID",
  },
  status: {
    type: "select",
    placeholder: "Choose status",
    options: [
      { value: "AVAILABLE", text: "Available" },
      { value: "BOOKED", text: "Booked" },
      { value: "EXPIRED", text: "Expired" },
      { value: "SINGLE_REQUEST_PENDING", text: "Single Request Pending" },
    ],
  },
  coachType: {
    type: "select",
    placeholder: "Choose coach type",
    options: [
      { value: "PRO", text: "Pro" },
      { value: "VOLUNTEER", text: "Volunteer" },
    ],
  },
  langCode: {
    type: "input",
    placeholder: "Enter ISO lang code (e.g. en, ar)",
  },
};

export const SlotsSearch = ({ updateQuery, initialQuery = {} }: Props) => {
  const items = Object.keys(searchItems) as (keyof typeof searchItems)[];
  const [selected, setSelected] = useState<keyof typeof searchItems>(items[0]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<SlotsFilter>(initialQuery);
  const [activeBadges, setActiveBadges] = useState<{ key: keyof typeof searchItems; label: string; value: string }[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const previousSelectedRef = useRef<keyof typeof searchItems | null>(null);

  const currentConfig = searchItems[selected];

  // Debounce
  const debounceRef = useRef<NodeJS.Timeout>();

  const handleSearch = useCallback(
    (key: keyof typeof searchItems, value: string) => {
      const newFilters: SlotsFilter = { ...currentFilters };

      if (value) (newFilters as any)[key] = value;
      else delete (newFilters as any)[key];

      setCurrentFilters(newFilters);
      updateQuery(newFilters);

      // Update badges based on the new filters (only string-valued entries)
      const badges: { key: keyof typeof searchItems; label: string; value: string }[] = [];
      Object.entries(newFilters).forEach(([filterKey, filterValue]) => {
        if (!(filterKey in searchItems)) return;
        if (typeof filterValue !== "string" || !filterValue) return;
        const searchKey = filterKey as keyof typeof searchItems;
        const config = searchItems[searchKey];
        const prettyValue = config.type === "select"
          ? (config.options?.find((o) => o.value === filterValue)?.text || filterValue)
          : filterValue;
        badges.push({ key: searchKey, label: String(searchKey), value: prettyValue });
      });
      
      setActiveBadges(badges);
    },
    [currentFilters, updateQuery]
  );

  const debounceSearch = useCallback(
    (key: keyof typeof searchItems, value: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => handleSearch(key, value), 300);
    },
    [handleSearch]
  );

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    setCurrentFilters(initialQuery);
    
    // Update badges based on current filters
    const badges: { key: keyof typeof searchItems; label: string; value: string }[] = [];
    
    Object.entries(initialQuery).forEach(([key, value]) => {
      if (!(key in searchItems)) return;
      if (typeof value !== "string" || !value) return;
      const searchKey = key as keyof typeof searchItems;
      const config = searchItems[searchKey];
      const prettyValue = config.type === "select"
        ? (config.options?.find((o) => o.value === value)?.text || value)
        : value;
      badges.push({ key: searchKey, label: String(searchKey), value: prettyValue });
    });
    
    setActiveBadges(badges);
  }, [initialQuery]);

  useEffect(() => {
    if (previousSelectedRef.current !== null && previousSelectedRef.current !== selected) {
      setQuery("");
      handleSearch(selected, "");
    }
    previousSelectedRef.current = selected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]); // Remove handleSearch from dependencies to prevent loops

  const handleInputChange = (value: string) => {
    setQuery(value);
    if (currentConfig.type === "input") debounceSearch(selected, value.trim());
    else handleSearch(selected, value);
  };

  const renderInputField = () => {
    if (currentConfig.type === "select" && currentConfig.options) {
      return (
        <select
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="w-full px-6 py-4 text-slate-800 bg-transparent focus:outline-none text-lg border-none focus:text-emerald-700 transition-colors duration-200"
       >
          <option value="">{currentConfig.placeholder}</option>
          {currentConfig.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        id="search-dropdown"
        type="search"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={currentConfig.placeholder}
        className="w-full px-6 py-4 text-slate-800 placeholder-slate-500 bg-transparent focus:border-none focus:outline-none focus:ring-0 text-lg flex-grow border-none focus:text-emerald-700 transition-colors duration-200"
      />
    );
  };

  return (
    <div className="flex items-center justify-center">
      <div className="w-full max-w-4xl mx-auto">
        <div ref={ref} className="relative group">
          <label htmlFor="search-dropdown" className="sr-only">
            Search category
          </label>

          <div
            className={`
              flex rounded-2xl overflow-hidden transition-all duration-300 ease-out
              bg-white/95 backdrop-blur-sm border-2 shadow-xl
              ${
                focused || open
                  ? "border-emerald-400 shadow-2xl shadow-emerald-200/60 scale-[1.02] ring-4 ring-emerald-100/50"
                  : "border-slate-200 hover:border-emerald-300 hover:shadow-2xl hover:scale-[1.01]"
              }
            `}
          >
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className={`
                  flex items-center px-6 py-4 bg-gradient-to-r transition-all duration-200
                  ${
                    open
                      ? "from-emerald-500 via-teal-500 to-cyan-500 text-white shadow-lg"
                      : "from-slate-100 via-slate-50 to-emerald-50 text-slate-700 hover:from-emerald-100 hover:via-teal-100 hover:to-cyan-100 hover:text-emerald-800"
                  }
                  border-r border-slate-200 font-medium 
                `}
            >
              <span className="truncate capitalize">{selected}</span>
              <FaChevronDown
                className={`
                  ml-3 w-3 h-3 transition-transform duration-200
                  ${open ? "rotate-180" : ""}
                `}
              />
            </button>

            <div className="flex-1 relative">
              {renderInputField()}
              {focused && (
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/40 via-teal-50/40 to-cyan-50/40 pointer-events-none border-none" />
              )}
            </div>

            {query.trim() && (
              <div className="px-4 py-4 flex items-center justify-center text-emerald-500">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
          </div>

          <div
            className={`
              absolute top-full left-0 mt-2 z-50 transition-all duration-300 ease-out origin-top w-auto
              bg-white/98 backdrop-blur-sm border border-emerald-200 rounded-2xl shadow-2xl overflow-hidden
              ${open ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2 pointer-events-none"}
            `}
          >
            <ul className="pl-0 list-none m-0 text-sm">
              {items.map((item, index) => (
                <li key={item}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(item);
                      setOpen(false);
                    }}
                    className={`
                        w-full px-20 py-3 text-left transition-all duration-150 flex items-center block
                        ${
                          selected === item
                            ? "bg-gradient-to-r from-emerald-100 via-teal-100 to-cyan-100 text-emerald-800 font-medium border-r-4 border-emerald-400 shadow-sm"
                            : "text-slate-700 hover:bg-gradient-to-r hover:from-emerald-50 hover:via-teal-50 hover:to-cyan-50 hover:text-emerald-700"
                        }
                      `}
                    style={{
                      animationDelay: `${index * 50}ms`,
                    }}
                  >
                    <span className="flex-1 capitalize">{item}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

      {/* Active filter badges */}
      {activeBadges.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {activeBadges.map((b) => (
            <button
              key={`${b.key}-${b.value}`}
              onClick={() => handleSearch(b.key, "")}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs hover:bg-emerald-100"
              title={`Remove ${b.label}`}
            >
              <span className="font-medium capitalize">{b.label}:</span>
              <span>{b.value}</span>
              <span className="ml-1">×</span>
            </button>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

